/* PreNumbers Financial Builder — Fixed Costs (reskinned).
   REPLACES: src/components/FinancialBuilder/FixedCostsPage/FixedCostsPage.jsx

   Used both as a standalone page (showNavigationControls) and inside the
   Fine-tune modal. All DB calls and handlers are unchanged. */

import { useState, useContext } from 'react';
import { DeleteOutlined } from '@ant-design/icons';

import { FinancialBuilderContext } from '../FinancialBuilderPage.jsx';
import { genericPOSTDatabaseCall, genericDELETEDatabaseCall } from '../../helperFunctions.jsx';
import { PageHead, FbIcons } from '../fbUI.jsx';

const { ArrowLeftOutlined, ArrowRightOutlined, PlusOutlined } = FbIcons;

async function addFixedCostToDatabase(fixedCost) { return genericPOSTDatabaseCall('/api/fb/addFixedCost', { fixedCost }, 'fixed cost'); }
async function addFixedCostYearToDatabase(fixedCostYear) { return genericPOSTDatabaseCall('/api/fb/addFixedCostYear', { fixedCostYear }, 'fixed cost year'); }
async function deleteFixedCostFromDatabase(fixedCost) { return genericDELETEDatabaseCall('/api/fb/deleteFixedCost', { id: fixedCost.id, company_id: fixedCost.company_id }, 'fixed cost'); }
async function deleteFixedCostYearFromDatabase(y) { return genericDELETEDatabaseCall('/api/fb/deleteFixedCostYear', { id: y.id, fixed_cost_id: y.fixed_cost_id }, 'fixed cost year'); }
async function saveFixedCostToDatabase(fixedCost) { return genericPOSTDatabaseCall('/api/fb/saveFixedCost', { fixedCost }, 'fixed cost'); }

const num = (v) => parseFloat(String(v ?? '').replace(/,/g, '')) || 0;

function FixedCostItem({ fixedCost, setCompanySelected, messageApi }) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const onYearChange = (yearId, newValue) => setCompanySelected((prev) => ({
    ...prev, fixed_costs: prev.fixed_costs.map((fc) => ({ ...fc, years: fc.years.map((y) => (y.id === yearId ? { ...y, amount: newValue } : y)) })),
  }));
  const onYearBlur = (yearId, newValue) => {
    const regex = /^(\d{0,13}(\.\d{0,2})?)?$/;
    if (!regex.test(newValue)) { messageApi.error('Invalid input. Max 13 digits before decimal and 2 after.'); newValue = '0.00'; }
    if (newValue === '') { messageApi.error('This field cannot be empty'); newValue = '0.00'; }
    onYearChange(yearId, newValue);
  };
  const deleteYear = async (year) => {
    messageApi.loading('Deleting fixed cost year...', 0);
    try {
      await deleteFixedCostYearFromDatabase(year);
      setCompanySelected((prev) => ({ ...prev, fixed_costs: prev.fixed_costs.map((fc) => ({ ...fc, years: fc.years.filter((y) => y.id !== year.id) })) }));
      messageApi.destroy(); messageApi.success('Fixed cost year deleted successfully');
    } catch (e) { console.error('Error deleting fixed cost year:', e); messageApi.destroy(); messageApi.error('Failed to delete fixed cost year'); }
  };
  const addYear = async (fixedCostId) => {
    messageApi.info('Adding new year to fixed cost...');
    try {
      const newYear = await addFixedCostYearToDatabase({ fixed_cost_id: fixedCostId });
      setCompanySelected((prev) => ({ ...prev, fixed_costs: prev.fixed_costs.map((fc) => (fc.id === fixedCostId ? { ...fc, years: [...fc.years, newYear] } : fc)) }));
      messageApi.destroy(); messageApi.success('New year added successfully');
    } catch (e) { console.error('Error adding new year:', e); messageApi.destroy(); messageApi.error('Failed to add new year'); }
  };
  const save = async () => {
    setSaving(true); messageApi.loading('Saving fixed cost...', 0);
    try { await saveFixedCostToDatabase(fixedCost); messageApi.destroy(); messageApi.success('Fixed cost saved successfully'); }
    catch (e) { console.error('Error saving fixed cost:', e); messageApi.destroy(); messageApi.error('Failed to save fixed cost'); }
    setSaving(false);
  };
  const del = async () => {
    setDeleting(true); messageApi.loading('Deleting fixed cost...', 0);
    try {
      await deleteFixedCostFromDatabase(fixedCost);
      setCompanySelected((prev) => ({ ...prev, fixed_costs: prev.fixed_costs.filter((fc) => fc.id !== fixedCost.id) }));
      messageApi.destroy(); messageApi.success('Fixed cost deleted successfully');
    } catch (e) { console.error('Error deleting fixed cost:', e); messageApi.destroy(); messageApi.error('Failed to delete fixed cost'); }
    setDeleting(false);
  };
  const changeName = (e) => setCompanySelected((prev) => ({ ...prev, fixed_costs: prev.fixed_costs.map((fc) => (fc.id === fixedCost.id ? { ...fc, name: e.target.value } : fc)) }));

  return (
    <div className="pn-fc-item">
      <div className="pn-fc-top">
        <input className="pn-control pn-fc-name" value={fixedCost.name} onChange={changeName} placeholder="Fixed cost name" />
        <div style={{ flex: 1 }} />
        <button type="button" className="pn-btn pn-btn-danger pn-btn-sm" disabled={deleting} onClick={del}><DeleteOutlined /> Delete</button>
        <button type="button" className="pn-btn pn-btn-accent pn-btn-sm" disabled={saving} onClick={save}>Save</button>
      </div>
      <div className="pn-fc-years">
        {fixedCost.years.map((y, i) => (
          <div key={y.id} className="pn-fc-year">
            <label>Year {i} <DeleteOutlined className="delete-year-icon" onClick={() => deleteYear(y)} /></label>
            <div className="pn-input-wrap has-pre"><span className="pre">$</span>
              <input className="pn-control" type="text" value={y.amount}
                onChange={(e) => onYearChange(y.id, e.target.value)} onBlur={(e) => onYearBlur(y.id, e.target.value)} /></div>
          </div>
        ))}
        <button type="button" className="pn-btn pn-btn-ghost pn-btn-sm" style={{ alignSelf: 'end' }} onClick={() => addYear(fixedCost.id)}><PlusOutlined /> Year</button>
      </div>
    </div>
  );
}

function FixedCostsPage({ showNavigationControls = true, contextToUse }) {
  const { goTo, companySelected, setCompanySelected, messageApi } = useContext(contextToUse || FinancialBuilderContext);
  const [fixedCostName, setFixedCostName] = useState('');

  const addNewFixedCost = async () => {
    if (!fixedCostName) { messageApi.error('Please enter a fixed cost name'); return; }
    messageApi.loading('Adding new fixed cost...');
    try {
      const newFixedCost = await addFixedCostToDatabase({ name: fixedCostName, company_id: companySelected.fb_id !== undefined ? companySelected.fb_id : companySelected.id });
      setCompanySelected((prev) => ({ ...prev, fixed_costs: [...prev.fixed_costs, newFixedCost] }));
      setFixedCostName('');
      messageApi.destroy(); messageApi.success('Fixed cost added successfully');
    } catch (e) { console.error('Error adding fixed cost:', e); messageApi.destroy(); messageApi.error('Failed to add fixed cost'); }
  };

  const fcs = companySelected.fixed_costs || [];
  const maxYears = Math.max(0, ...fcs.map((fc) => fc.years.length));
  const totals = Array.from({ length: maxYears }).map((_, i) => fcs.reduce((s, fc) => s + num(fc.years[i]?.amount), 0));

  const body = (
    <>
      <div className="pn-fc-list">
        {fcs.length === 0 && <div className="pn-fc-empty">No fixed costs yet. Add one below.</div>}
        {fcs.map((fc) => <FixedCostItem key={fc.id} fixedCost={fc} setCompanySelected={setCompanySelected} messageApi={messageApi} />)}
      </div>
      {totals.length > 0 && (
        <div className="pn-fc-total"><span>Total / year</span>
          <div className="pn-fc-total-years">{totals.map((t, i) => <span key={i}>${t.toLocaleString()}</span>)}</div></div>
      )}
      <div className="pn-fc-add">
        <input className="pn-control" placeholder="New fixed cost name" value={fixedCostName}
          onChange={(e) => setFixedCostName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNewFixedCost()} />
        <button type="button" className="pn-btn pn-btn-ghost" onClick={addNewFixedCost}><PlusOutlined /> Add fixed cost</button>
      </div>
    </>
  );

  if (!showNavigationControls) return <div className="pn-modal-embed">{body}</div>;

  return (
    <div className="pn-wrap">
      <PageHead eyebrow={companySelected.name} title="Fixed Costs"
        sub={<><b>{fcs.length}</b> recurring cost lines.</>}>
        <button type="button" className="pn-btn pn-btn-ghost" onClick={() => goTo(2)}><ArrowLeftOutlined /> Back</button>
        <button type="button" className="pn-btn pn-btn-cta" onClick={() => goTo(3)}>Next <ArrowRightOutlined /></button>
      </PageHead>
      {body}
    </div>
  );
}

export default FixedCostsPage;
